/**
 * Substrate rendering and behavior definitions.
 *
 * Each substrate has its own clearly-marked section below.
 * To work on a substrate, find its section — all logic for that substrate
 * lives here and nowhere else.
 *
 * To add a new substrate:
 *   1. Add a section below with the substrate's logic functions
 *   2. Add one entry to REGISTRY at the bottom of this file
 *   3. Nothing else needs to change
 *
 * Exported API:
 *   resolveSubstrateBackData(substrateName, getOptionValue)  → object|null
 *   resolveCustomSizesAllowed(substrateName)                 → boolean
 *   resolveArtworkRenderOptions(substrateName, backData)     → object|null
 *   resolveShowBackPanelWithFrame(substrateName, backData)   → boolean
 *
 * getOptionValue signature: (pattern: RegExp) => string|null
 */

function parseInches(val) {
  if (val == null) return null
  const s = String(val).replace(/["']/g, '').trim()
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)/)
  if (frac) return Number(frac[1]) / Number(frac[2])
  const parsed = parseFloat(s)
  return isNaN(parsed) ? null : parsed
}


// ─── PRINT ───────────────────────────────────────────────────────────────────
// Standard paper/photo print. No back panel, no special rendering options.

const print = {}


// ─── CANVAS ──────────────────────────────────────────────────────────────────
// Stretched canvas. Back panel is handled via stretcher/float-frame props on
// FrameRenderer, not via substrateBackData.

const canvas = {}


// ─── ALUMINUM ────────────────────────────────────────────────────────────────
// Metal panel. Reads aluminum material option for white vs silver finish.
// Border inset is read from the Border option.

const aluminum = {
  getSubstrateBackData(getOptionValue) {
    const material = getOptionValue(/aluminum\s*material/i) || ''
    const isWhite = /white/i.test(material)
    const border = parseInches(getOptionValue(/border/i)) ?? 0
    return { depth: 0.25, color: isWhite ? 0xffffff : 0xc0c0c0, roughness: 0.4, metalness: 0.6, hasSides: true, border }
  },
}


// ─── ACRYLIC ─────────────────────────────────────────────────────────────────
// Clear or colored acrylic panel. Depth and color derived from "Acrylic
// Substrate" option value. Border inset from Border option.

function acrylicColorFromName(val) {
  if (!val) return null
  const s = String(val).toLowerCase()
  if (/black/i.test(s)) return 0x2a2a2a
  if (/white/i.test(s)) return 0xffffff
  if (/clear|transparent/i.test(s)) return 0xb8d4e3
  if (/silver|grey|gray/i.test(s)) return 0xc0c0c0
  if (/bronze/i.test(s)) return 0xcd7f32
  if (/gold/i.test(s)) return 0xd4af37
  return null
}

const acrylic = {
  getSubstrateBackData(getOptionValue) {
    const acrylicVal = getOptionValue(/acrylic\s*substrate/i)
    const depth = parseInches(acrylicVal) ?? 0.25
    const border = parseInches(getOptionValue(/border/i)) ?? 0
    return { depth, color: acrylicColorFromName(acrylicVal) ?? 0xb8d4e3, roughness: 0.1, metalness: 0.3, hasSides: true, border }
  },
}


// ─── ACOUSTIC PANEL ──────────────────────────────────────────────────────────
// Fabric-wrapped panel (same 3D construction as stretched canvas: wrap mesh +
// weave normal). `depth` is the wrap thickness; color/roughness apply to the
// solid back plane inside the wrap group.

const acousticPanel = {
  getSubstrateBackData() {
    return { depth: 0.25, color: 0xf5f5f5, roughness: 0.7, metalness: 0 }
  },
}


// ─── GATOR BOARD ─────────────────────────────────────────────────────────────
// Foam-core gator board. Dark gray with sides.

const gatorBoard = {
  getSubstrateBackData() {
    return { depth: 0.125, color: 0x5a5a5a, roughness: 0.6, metalness: 0, hasSides: true }
  },
}


// ─── MURAL ───────────────────────────────────────────────────────────────────
// Printed directly on the substrate. Always shows back panel (even when
// framed). No custom sizes — printed to specific dimensions.

const mural = {
  getSubstrateBackData() {
    return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
  },
  allowsCustomSizes() { return false },
  showBackPanelWithFrame() { return true },
}


// ─── WINDOW FILM ─────────────────────────────────────────────────────────────
// Adhesive film applied to glass. Behavior varies by material:
//   opaque   — solid panel (like a print)
//   transparent/translucent/perforated — artwork plane is double-sided and
//   partially transparent, back panel depth is 0
// No custom sizes — ordered to specific dimensions.

const windowFilm = {
  getSubstrateBackData(getOptionValue) {
    const material = (getOptionValue(/material/i) || getOptionValue(/window\s*film/i) || '').toLowerCase()
    const isOpaque = /opaque/i.test(material)
    const isTransparent = /transparent/i.test(material)
    const isTranslucent = /translucent/i.test(material)
    const isPerforated = /perforat/i.test(material)
    if (isOpaque) return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
    return {
      depth: 0,
      useBackface: isTransparent || isTranslucent || isPerforated,
      opacity: isTransparent ? 0.65 : isTranslucent ? 0.9 : 1,
      perforated: isPerforated,
      holesPerInch: 4,
      holeSizeInches: 0.0625,
    }
  },
  allowsCustomSizes() { return false },
  getArtworkRenderOptions(backData) {
    if (!backData) return null
    return {
      side: backData.useBackface ? 'DoubleSide' : 'FrontSide',
      opacity: backData.opacity ?? 1,
      transparent: (backData.opacity ?? 1) < 1,
      perforated: backData.perforated ?? false,
    }
  },
  showBackPanelWithFrame(backData) {
    return backData?.depth > 0
  },
}


// ─── PVC ─────────────────────────────────────────────────────────────────────
// PVC/Sintra board. No special rendering options defined yet.

const pvc = {}


// ─── CUSTOM ──────────────────────────────────────────────────────────────────
// Custom substrate. No special rendering options defined yet.

const custom = {}


// ─── REGISTRY ────────────────────────────────────────────────────────────────
// Ordered pattern-matching — first match wins. More specific patterns go first.
// Substrate names come from the API and may vary in casing/phrasing, hence
// regex rather than exact string matching.

const REGISTRY = [
  { match: /acoustic/i,      module: acousticPanel },
  { match: /gator/i,         module: gatorBoard },
  { match: /window\s*film/i, module: windowFilm },
  { match: /aluminum/i,      module: aluminum },
  { match: /acrylic/i,       module: acrylic },
  { match: /mural/i,         module: mural },
  { match: /canvas/i,        module: canvas },
  { match: /pvc/i,           module: pvc },
  { match: /print/i,         module: print },
  { match: /custom/i,        module: custom },
]

function findModule(substrateName) {
  if (!substrateName) return null
  const entry = REGISTRY.find(e => e.match.test(substrateName))
  return entry ? entry.module : null
}

export function resolveSubstrateBackData(substrateName, getOptionValue) {
  const mod = findModule(substrateName)
  if (mod && typeof mod.getSubstrateBackData === 'function') {
    return mod.getSubstrateBackData(getOptionValue)
  }
  return null
}

export function resolveCustomSizesAllowed(substrateName, defaultAllowed = true) {
  const mod = findModule(substrateName)
  if (mod && typeof mod.allowsCustomSizes === 'function') {
    return mod.allowsCustomSizes()
  }
  return defaultAllowed
}

export function resolveArtworkRenderOptions(substrateName, backData) {
  const mod = findModule(substrateName)
  if (mod && typeof mod.getArtworkRenderOptions === 'function') {
    return mod.getArtworkRenderOptions(backData)
  }
  return null
}

export function resolveShowBackPanelWithFrame(substrateName, backData) {
  const mod = findModule(substrateName)
  if (mod && typeof mod.showBackPanelWithFrame === 'function') {
    return mod.showBackPanelWithFrame(backData)
  }
  return false
}
